<template>
  <v-row no-gutters>
    <v-col cols="12" v-if="isFetched && !error">
      <card-title-row is-subtitle translation-path="sidebar.inspections.inspections" class="pb-2">
        <template #buttons>
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="
              isFeatureEditing
                ? $i18n.t('sidebar.inspections.finishFeatureEditing')
                : $i18n.t('sidebar.inspections.addFeature')
            "
            @click="openInspection('new')"
            :disabled="isFeatureEditing"
          />
        </template>
      </card-title-row>
      <sidebar-row v-if="inspections.length" icon="mdi-calendar-alert">
        <v-row no-gutters v-if="intervalValue" align="center">
          <v-col cols="12">
            <v-row no-gutters align="center">
              <v-col cols="auto" :class="`${nextInspectionDateColor}--text text-subtitle-2 pr-1 px-0`">
                {{ nextInspectionDateFormatted }}
              </v-col>
              <v-col cols="auto" class="text-caption pa-0 font--text">
                {{ `(${nextInspectionDaysRemaingFormatted})` }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row no-gutters align="center">
          <v-col cols="12">
            <v-row no-gutters align="center">
              <v-col cols="auto" class="text-truncate text-caption font-italic font-weight-light">
                {{ `${lastInspectionDateFormated}` }}
              </v-col>
              <v-col cols="auto" class="pl-3">
                <link-underlined
                  @click="toggleInspectionsList"
                  :translation-path="inspectionsListVisible ? 'featureCard.hide' : 'featureCard.seeAll'"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row no-gutters v-if="inspectionsListVisible" class="pt-7">
          <inspections-list
            :items="inspections"
            @openInspection="openInspection"
            :date-key-path="`properties.${inspectionsSetting?.relation_date}`"
          />
        </v-row>
      </sidebar-row>
      <sidebar-row v-else>
        <v-col cols="12" class="pa-0 text-left text-truncate text-caption font-italic font-weight-light">
          {{ $i18n.t('sidebar.noInspections') }}
        </v-col>
      </sidebar-row>
      <feature-card-inspection-dialog
        v-if="isInspectionLoaded"
        :is-visible.sync="isInspectionVisible"
        :layer="currentInspectionLayer"
        :feature-id="currentInspectionId"
        @inspectionAdded="init"
        @inspectionEdited="init"
        :form-constant-values="formConstantValues"
      />
    </v-col>
    <main-loader v-else-if="!isFetched && !error" />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import InspectionsList from '@/components/InspectionsList';
import LinkUnderlined from '@/components/LinkUnderlined';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'FeatureCardInspectionsWidget',
  components: {
    CardTitleRow,
    FeatureCardInspectionDialog: () => import('@/components/FeatureCardInspectionDialog'),
    InspectionsList,
    LinkUnderlined,
    MainLoader,
    SidebarRow,
  },
  props: {
    layerId: {
      type: Number,
      required: true,
    },
    featureId: {
      type: Number,
      required: true,
    },
    isFeatureEditing: {
      type: Boolean,
      default: false,
    },
    values: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    inspectionsSettings: get('inspections/inspections'),
    inspectionsSettingsArray() {
      return this.$_objectToArray(this.inspectionsSettings);
    },
    inspectionsSetting() {
      return this.inspectionsSettingsArray?.find(inspection => inspection.main_layer === this.layerId) || undefined;
    },

    currentInspectionLayer() {
      return this.layers[this.inspectionsSetting?.relation_layer];
    },
    formConstantValues() {
      return { [this.inspectionsSetting?.relation_attribute]: this.featureId };
    },

    lastInspection() {
      return this.inspections[0];
    },
    lastInspectionDateValue() {
      return this.lastInspection?.properties[this.inspectionsSetting?.relation_date] || null;
    },
    lastInspectionDateFormated() {
      return this.lastInspectionDateValue
        ? `${this.$i18n.t('featureCard.lastInspection')}: ${this.$dayjs(this.lastInspectionDateValue).format(
            'DD.MM.YYYY'
          )}`
        : '';
    },

    nextInspectionDateValue() {
      return this.lastInspectionDateValue
        ? this.$dayjs(this.lastInspectionDateValue).add(this.intervalValue || 365, 'd')
        : null;
    },
    nextInspectionDateFormatted() {
      return this.nextInspectionDateValue ? this.nextInspectionDateValue.format('DD.MM.YYYY') : '';
    },
    nextInspectionDaysRemaining() {
      const today = this.$dayjs(this.$dayjs().format('DD.MM.YYYY'), 'DD.MM.YYYY');
      return this.nextInspectionDateValue ? this.nextInspectionDateValue.diff(today, 'd') : null;
    },
    nextInspectionDaysRemainingAbs() {
      return Math.abs(this.nextInspectionDaysRemaining);
    },
    nextInspectionDaysRemaingFormatted() {
      const a = this.$i18n.tc('default.days', this.nextInspectionDaysRemainingAbs);
      if (this.nextInspectionDaysRemaining === 0) {
        return `${this.$i18n.t('default.today')}`;
      } else if (this.nextInspectionDaysRemaining > 0) {
        return `${this.$i18n.t('sidebar.for')} ${this.nextInspectionDaysRemainingAbs} ${a}`;
      } else {
        return `${this.nextInspectionDaysRemainingAbs} ${a} ${this.$i18n.t('sidebar.afterDeadline')}`;
      }
    },
    nextInspectionDateColor() {
      if (this.nextInspectionDaysRemaining < 0) {
        return 'error';
      } else if (this.warningValue && this.nextInspectionDaysRemaining < this.warningValue) {
        return 'edit';
      }
      return 'success';
    },

    intervalValue() {
      return this.values?.[this.inspectionsSetting?.interval_attribute] || null;
    },
    warningValue() {
      return this.values?.[this.inspectionsSetting?.deadline_attribute] || null;
    },
  },
  data: () => ({
    currentInspectionId: null,

    inspections: [],
    inspectionsListVisible: false,
    isFetched: false,

    isInspectionLoaded: false,
    isInspectionVisible: false,

    error: false,
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getInspectionsSettings: call('inspections/getInspections'),
    getInspectionsFilter(layerId, attribute, featuresIds = [this.featureId]) {
      const dS = this.dataSources[this.layers[layerId]?.data_source_name];
      return {
        $IN: {
          [dS.attributes_use_datasource_qualified_names ? attribute : `${dS.name}.${attribute}`]: featuresIds,
        },
      };
    },
    openInspection(inspectionId) {
      if (this.isFeatureEditing) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('sidebar.inspections.finishFeatureEditing'),
        });
        return;
      }
      this.currentInspectionId = inspectionId;
      this.isInspectionLoaded = true;
      this.isInspectionVisible = true;
    },
    toggleInspectionsList() {
      this.inspectionsListVisible = !this.inspectionsListVisible;
    },
    async init() {
      this.isFetched = false;
      if (!this.inspectionsSettingsArray?.length || !this.inspectionsSetting) {
        await this.getInspectionsSettings();
      }
      if (!this.inspectionsSetting) {
        this.error = true;
        this.isFetched = true;
        return;
      }
      const {
        relation_attribute: relationAttribute,
        relation_date: relationDateAttribute,
        relation_layer: relationLayer,
      } = this.inspectionsSetting;
      const dS = this.dataSources[this.layers[relationLayer]?.data_source_name];
      if (!dS) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.noPermisionsToInspections'),
        });
        this.error = true;
        this.isFetched = true;
        return;
      }
      const features_filter = this.getInspectionsFilter(relationLayer, relationAttribute);
      const payload = {
        layer_id: relationLayer,
        features_filter,
        params: {
          with_features: true,
          with_collection_bbox: false,
          with_count: false,
          with_geometry: false,
        },
      };
      const features = await this.getLayerFeatures(payload);
      this.inspections = features.data.data.features.features
        .filter(feature => {
          return feature.properties[relationDateAttribute] ? true : false;
        })
        .sort((a, b) => {
          return this.$dayjs(b.properties[relationDateAttribute]).diff(
            this.$dayjs(a.properties[relationDateAttribute])
          );
        });
      this.isFetched = true;
      this.error = false;
    },
  },
  watch: {
    isInspectionVisible(nV) {
      if (!nV) {
        this.currentInspectionId = null;
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>
